import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

import 'tom-select/dist/css/tom-select.css';

export default class extends Controller {
  static targets = ['tableBody', 'hiddenField'];
  static values = { initialData: Object };

  // Fields built via buildSelectCell that should get a searchable (Tom Select)
  // dropdown instead of a plain native <select> — override in subclasses that
  // have a long option list (e.g. `searchableFields = ['country']`).
  searchableFields = [];

  addRow(event) {
    event.preventDefault();
    this.rows.push(this.emptyRow());
    this.renderRows();
    this.syncHiddenField();
  }

  removeRow(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.rowIndex, 10);
    this.rows.splice(index, 1);
    if (this.rows.length === 0) this.rows = [this.emptyRow()];
    this.renderRows();
    this.syncHiddenField();
  }

  disconnect() {
    this.destroySearchableSelects();
  }

  renderRows() {
    this.destroySearchableSelects();
    const tbody = this.tableBodyTarget;
    tbody.innerHTML = '';
    this.rows.forEach((row, index) => tbody.appendChild(this.buildRow(row, index)));
    this.initSearchableSelects();
  }

  initSearchableSelects() {
    this.searchableFields.forEach((field) => {
      this.tableBodyTarget.querySelectorAll(`select[data-field="${field}"]`).forEach((select) => {
        const instance = new TomSelect(select, {
          maxOptions: null,
          allowEmptyOption: true,
          dropdownParent: 'body',
        });
        instance.on('dropdown_open', () => this.alignDropdownToTable(instance));
      });
    });
  }

  destroySearchableSelects() {
    this.searchableFields.forEach((field) => {
      this.tableBodyTarget
        ?.querySelectorAll(`select[data-field="${field}"]`)
        .forEach((select) => select.tomselect?.destroy());
    });
  }

  alignDropdownToTable(instance) {
    const rect = this.tableBodyTarget.getBoundingClientRect();
    instance.dropdown.style.width = `${rect.width}px`;
    instance.dropdown.style.left = `${rect.left + window.scrollX}px`;
  }

  buildSelectCell(row, index, field, options, { fullWidth = false } = {}) {
    const td = document.createElement('td');
    const wrapper = document.createElement('div');
    wrapper.className = 'select-wrapper w-100';
    if (fullWidth) wrapper.style.display = 'block';
    const select = document.createElement('select');
    if (fullWidth) select.style.maxWidth = 'none';
    select.dataset.rowIndex = index;
    select.dataset.field = field;
    select.dataset.action = `change->${this.identifier}#updateField`;

    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = '—';
    select.appendChild(blank);

    options.forEach((optText) => {
      const opt = document.createElement('option');
      opt.value = optText;
      opt.textContent = optText;
      if (row[field] === optText) opt.selected = true;
      select.appendChild(opt);
    });

    wrapper.appendChild(select);
    td.appendChild(wrapper);
    return td;
  }

  buildStringCell(row, index, field, placeholder) {
    const td = document.createElement('td');
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    input.className = 'form-control w-100';
    input.dataset.rowIndex = index;
    input.dataset.field = field;
    input.dataset.action = `input->${this.identifier}#updateField`;
    input.value = row[field] ?? '';
    td.appendChild(input);
    return td;
  }

  buildRemoveButton(index) {
    const td = document.createElement('td');
    td.className = 'text-center';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button empty button-small button-tertiary';
    btn.dataset.action = `click->${this.identifier}#removeRow`;
    btn.dataset.rowIndex = index;
    btn.innerHTML = '&times;';
    td.appendChild(btn);
    return td;
  }

  syncHiddenField() {
    if (!this.hasHiddenFieldTarget) return;
    this.hiddenFieldTarget.value = JSON.stringify({ rows: this.rows });
  }
}
